import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, ICONS, ConfirmButton, errMsg } from './extUi';

// Teams of an external tournament (tournament page). The veto link picks from these teams.

export default function ExtTeamsCard({ tournamentId, onCount }) {
  const { t } = useTranslation();
  const [teams, setTeams] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`/api/v1/ext/tournaments/${tournamentId}/teams`, { params: showArchived ? { archived: 1 } : {} });
      setTeams(r.data.data || []); setError('');
      // the veto link needs 2+ active teams → let the page re-check it when the count changes
      if (!showArchived && onCount) onCount((r.data.data || []).length);
      else if (showArchived && onCount) axios.get(`/api/v1/ext/tournaments/${tournamentId}/teams`).then(a => onCount((a.data.data || []).length)).catch(() => {});
    } catch (e) { setError(errMsg(e, t('ext.loadFailed', 'Failed to load'))); }
  }, [tournamentId, showArchived, t, onCount]);
  useEffect(() => { load(); }, [load]);

  const unarchive = async (id) => {
    try { await axios.post(`/api/v1/ext/tournaments/${tournamentId}/teams/${id}/unarchive`); load(); } catch (e) { setError(errMsg(e, t('ext.saveFailed', 'Could not save'))); }
  };

  return (
    <section className="xt-sec">
      <div className="xt-sec-head">
        <h2>{t('extTeams.title', 'Teams')}</h2>
        {teams && !showArchived && <span className="xt-count">{teams.length}</span>}
        <span className="grow" />
        <button type="button" className="xt-btn" onClick={() => setShowArchived(!showArchived)}>{showArchived ? t('extTeams.showActive', 'Show active teams') : t('extTeams.showArchived', 'Show archived')}</button>
        {!showArchived && <Link to={`/admin/tournaments/${tournamentId}/teams/new`} className="xt-btn primary"><Icon d={ICONS.plus} /> {t('extTeams.addTeam', 'Add team')}</Link>}
      </div>
      <p className="xt-hint">{t('extTeams.hint', 'Each team has 5 active players and up to 3 substitutes, with their IGN and Steam ID, plus a default stage seating. The veto link picks from these teams; matches use the IGNs instead of Steam names.')}</p>
      {error && <div className="xt-msg bad">{error}</div>}
      {teams && teams.length === 0 && <div className="xt-empty">{showArchived ? t('extTeams.noArchived', 'No archived teams.') : t('extTeams.none', 'No teams yet. Add at least two teams to use the veto link.')}</div>}
      {teams && teams.length > 0 && (
        <div className="xt-list">
          {teams.map(tm => (
            <div key={tm.id} className="xt-row">
              <div className="xt-row-top">
                <span className="xt-row-name xt-team-name">
                  {tm.name}{tm.tag && <span className="tag">[{tm.tag}]</span>}
                </span>
                <span className="xt-roster">
                  {tm.seating.seats.map((s, i) => <span key={i}>{s.ign}</span>)}
                  {tm.seating.substitutes.length > 0 && <span className="subs">{t('extTeams.subsList', '(subs: {{names}})', { names: tm.seating.substitutes.map(s => s.ign).join(', ') })}</span>}
                </span>
                {showArchived ? (
                  <button type="button" className="xt-btn ok" onClick={() => unarchive(tm.id)}>{t('ext.unarchive', 'Unarchive')}</button>
                ) : (
                  <span className="xt-actions">
                    <Link to={`/admin/tournaments/${tournamentId}/teams/${tm.id}/seating`} className="xt-btn">{t('extTeams.seating', 'Seating')}</Link>
                    <Link to={`/admin/tournaments/${tournamentId}/teams/${tm.id}/edit`} className="xt-btn"><Icon d={ICONS.edit} /> {t('common.edit', 'Edit')}</Link>
                    <ConfirmButton label={t('ext.archive', 'Archive')} icon={ICONS.trash}
                      warning={t('extTeams.archiveWarning', 'The team leaves the list and the veto picker. Its matches keep their players and seating.')}
                      onConfirm={async () => { await axios.delete(`/api/v1/ext/tournaments/${tournamentId}/teams/${tm.id}`); load(); }} />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
